import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Mic, BookOpen, GraduationCap } from 'lucide-react';

export const ThreeWaysSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const handleClick = () => {
    window.scrollTo(0, 0);
  };

  return (
    <section
      data-testid="three-ways-section"
      ref={ref}
      className="py-16 md:py-24 lg:py-32 bg-[#F4F1EC]"
      id="programs"
    >
      <div className="container-peak">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-12 lg:mb-16"
        >
          <span className="section-tag block mb-4 lg:mb-6">How We Help</span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-light leading-[1.2] text-[#1F2A44]">
            Three ways to achieve{' '}
            <em className="not-italic text-[#D97904]">Peak Performance</em>
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto">
          {/* Live Experiences - Two links */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="bg-white p-6 lg:p-8 border border-[#E6E1DA] hover:border-[#D97904] transition-all group card-hover"
          >
            <div className="flex items-start justify-between mb-4 lg:mb-6">
              <div className="w-12 h-12 lg:w-14 lg:h-14 flex items-center justify-center border border-[#E6E1DA] group-hover:border-[#D97904] group-hover:bg-[#D97904] transition-all">
                <Mic className="w-5 h-5 lg:w-6 lg:h-6 text-[#5A6B7A] group-hover:text-white transition-colors" />
              </div>
              <span className="text-4xl lg:text-5xl font-['Cormorant_Garamond'] text-[#E6E1DA] group-hover:text-[#D97904]/20 transition-colors">
                01
              </span>
            </div>
            
            <h3 className="font-['Cormorant_Garamond'] text-xl lg:text-2xl text-[#1F2A44] mb-1">
              Live Experiences
            </h3>
            <p className="text-[#D97904] text-xs lg:text-sm font-['Syne'] uppercase tracking-wider mb-3 lg:mb-4">
              Workshops, Trainings & Keynotes
            </p>
            
            <p className="text-[#5A6B7A] text-sm leading-relaxed mb-4 lg:mb-6">
              High-impact sessions that transform how your team thinks, performs, and leads.
            </p>
            
            <div className="flex flex-col gap-2">
              <Link
                to="/keynotes"
                onClick={handleClick}
                className="inline-flex items-center gap-2 text-[#1F2A44] font-['Syne'] text-xs uppercase tracking-widest hover:text-[#D97904] hover:gap-3 transition-all"
              >
                Keynotes
                <ArrowRight size={14} />
              </Link>
              <Link
                to="/workshops"
                onClick={handleClick}
                className="inline-flex items-center gap-2 text-[#1F2A44] font-['Syne'] text-xs uppercase tracking-widest hover:text-[#D97904] hover:gap-3 transition-all"
              >
                Workshops & Training
                <ArrowRight size={14} />
              </Link>
            </div>
          </motion.div>

          {/* Peak 9 Library */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.35 }}
            className="bg-white p-6 lg:p-8 border border-[#E6E1DA] hover:border-[#D97904] transition-all group card-hover"
          >
            <div className="flex items-start justify-between mb-4 lg:mb-6">
              <div className="w-12 h-12 lg:w-14 lg:h-14 flex items-center justify-center border border-[#E6E1DA] group-hover:border-[#D97904] group-hover:bg-[#D97904] transition-all">
                <BookOpen className="w-5 h-5 lg:w-6 lg:h-6 text-[#5A6B7A] group-hover:text-white transition-colors" />
              </div>
              <span className="text-4xl lg:text-5xl font-['Cormorant_Garamond'] text-[#E6E1DA] group-hover:text-[#D97904]/20 transition-colors">
                02
              </span>
            </div>
            
            <h3 className="font-['Cormorant_Garamond'] text-xl lg:text-2xl text-[#1F2A44] mb-1">
              Peak 9 Library
            </h3>
            <p className="text-[#D97904] text-xs lg:text-sm font-['Syne'] uppercase tracking-wider mb-3 lg:mb-4">
              On-Demand Learning
            </p>
            
            <p className="text-[#5A6B7A] text-sm leading-relaxed mb-4 lg:mb-6">
              A curated collection of frameworks, tools, and insights for continuous growth.
            </p>
            
            <div className="flex flex-col gap-2">
              <Link
                to="/library"
                onClick={handleClick}
                className="inline-flex items-center gap-2 text-[#1F2A44] font-['Syne'] text-xs uppercase tracking-widest hover:text-[#D97904] hover:gap-3 transition-all"
              >
                Browse Library
                <ArrowRight size={14} />
              </Link>
              <span className="h-5"></span> {/* Spacer for alignment */}
            </div>
          </motion.div>

          {/* School of Shayamal */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="bg-white p-6 lg:p-8 border border-[#E6E1DA] hover:border-[#D97904] transition-all group card-hover"
          >
            <div className="flex items-start justify-between mb-4 lg:mb-6">
              <div className="w-12 h-12 lg:w-14 lg:h-14 flex items-center justify-center border border-[#E6E1DA] group-hover:border-[#D97904] group-hover:bg-[#D97904] transition-all">
                <GraduationCap className="w-5 h-5 lg:w-6 lg:h-6 text-[#5A6B7A] group-hover:text-white transition-colors" />
              </div>
              <span className="text-4xl lg:text-5xl font-['Cormorant_Garamond'] text-[#E6E1DA] group-hover:text-[#D97904]/20 transition-colors">
                03
              </span>
            </div>
            
            <h3 className="font-['Cormorant_Garamond'] text-xl lg:text-2xl text-[#1F2A44] mb-1">
              School of Shayamal
            </h3>
            <p className="text-[#D97904] text-xs lg:text-sm font-['Syne'] uppercase tracking-wider mb-3 lg:mb-4">
              Certification Program
            </p>
            
            <p className="text-[#5A6B7A] text-sm leading-relaxed mb-4 lg:mb-6">
              For coaches and practitioners who want to master the Peak Performance methodology.
            </p>
            
            <div className="flex flex-col gap-2">
              <Link
                to="/school"
                onClick={handleClick}
                className="inline-flex items-center gap-2 text-[#1F2A44] font-['Syne'] text-xs uppercase tracking-widest hover:text-[#D97904] hover:gap-3 transition-all"
              >
                Learn More
                <ArrowRight size={14} />
              </Link>
              <span className="h-5"></span> {/* Spacer for alignment */}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ThreeWaysSection;
